import React from 'react';
import { Plus } from 'lucide-react';
import SeedDataPanel from './plants/SeedDataPanel';
import PlantForm from './plants/PlantForm';
import PlantList from './plants/PlantList';

/**
 * PlantMasterView — thin orchestrator. The heavy lifting lives in:
 *   - plants/SeedDataPanel  : demo data seed/clear card
 *   - plants/PlantForm      : add + edit form (mode-driven theming)
 *   - plants/PlantList      : list of existing plants
 *
 * All mutating handlers (`handleAddPlant`, `handleEditPlant`, etc.) are still
 * owned by App.js — this view stays presentational + composition.
 */
const PlantMasterView = ({
  plants, showAddPlantForm, setShowAddPlantForm, newPlant, setNewPlant,
  handleAddPlant, handleDeletePlant, handleEditPlant, editingPlant,
  handleUpdatePlant, handleCancelEdit, loading, token, onReload,
}) => {
  const toggleAddForm = () => {
    setShowAddPlantForm(!showAddPlantForm);
    if (showAddPlantForm) {
      // Closing the form -> reset
      setNewPlant({ name: '', location: '', annual_capacity_mt: 0 });
    }
  };

  const cancelAdd = () => {
    setShowAddPlantForm(false);
    setNewPlant({ name: '', location: '', annual_capacity_mt: 0 });
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Plant Master Data</h2>
            <p className="text-sm text-gray-600 mt-1">Manage all plants in the system</p>
          </div>
          {!editingPlant && (
            <button
              onClick={toggleAddForm}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2.5 px-4 sm:px-6 min-h-[44px] rounded-lg shadow-lg transition-colors flex items-center whitespace-nowrap"
              data-testid="add-new-plant-btn"
            >
              <Plus className="h-5 w-5 mr-2" /> Add Plant
            </button>
          )}
        </div>
      </div>

      <SeedDataPanel token={token} onReload={onReload} />

      {showAddPlantForm && !editingPlant && (
        <PlantForm
          mode="add"
          plant={newPlant}
          setPlant={setNewPlant}
          onSubmit={handleAddPlant}
          onCancel={cancelAdd}
          loading={loading}
        />
      )}

      {editingPlant && (
        <PlantForm
          mode="edit"
          plant={newPlant}
          setPlant={setNewPlant}
          onSubmit={handleUpdatePlant}
          onCancel={handleCancelEdit}
          loading={loading}
        />
      )}

      <PlantList
        plants={plants}
        editingPlant={editingPlant}
        onEdit={handleEditPlant}
        onDelete={handleDeletePlant}
      />
    </div>
  );
};

export default PlantMasterView;
